import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Image,
  Text,
  VStack,
  useColorModeValue,
  Container,
  Heading,
  Flex,
} from '@chakra-ui/react';

// Define product categories with their details
const PRODUCT_CATEGORIES = [
  {
    type: 'Display',
    title: 'Display Calibration Devices',
    description: '',
    image: '/assets/Display-Devices.png',
    enabled: true
  },
  {
    type: 'Studio',
    title: 'Studio Calibration Devices',
    description: '',
    image: '/assets/Studio-Devices.png',
    enabled: true
  },
  {
    type: 'Photo Target',
    title: 'Photo Camera Targets',
    description: '',
    image: '/assets/Photo-Targets.png',
    enabled: true
  },
  {
    type: 'Video Target',
    title: 'Video Camera Targets',
    description: '',
    image: '/assets/Video-Targets.png',
    enabled: true
  },
  {
    type: 'Accessories',
    title: 'Calibrite Accessories',
    description: '',
    image: '/assets/Calibrite-Accessories.png',
    enabled: true
  }
];

function SelectProductType({ onSelect }) {
  const [selectedType, setSelectedType] = useState(null);
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const selectedBg = useColorModeValue('green.50', 'green.800');
  const selectedBorder = useColorModeValue('green.500', 'green.500');

  const handleSelect = (category) => {
    if (category.enabled) {
      setSelectedType(category.type);
      onSelect(category);
    }
  };

  return (
    <Box 
      py={4} 
      h={{ base: "auto", md: "100vh" }}
      maxH={{ base: "none", md: "800px" }}
      display="flex"
      flexDirection="column"
    >
      <Container 
        maxW="container.xl" 
        h="100%" 
        display="flex" 
        flexDirection="column"
      >
        <Heading 
          size="lg" 
          textAlign="center" 
          mb={{ base: 4, md: 8 }}
          flexShrink={0}
        >
          Select Product Type
        </Heading>

        <Box 
          overflow="auto" 
          flex="1"
          minH={0}
          display="flex"
          alignItems="center"
          mx={-2}
        >
          <Flex
            minW="min-content"
            minH="min-content"
            gap={{ base: 2, sm: 4, md: 6 }}
            justify="center"
            align="center"
            mx="auto"
            w="calc(100% - 8px)"
            h="100%"
            p={2}
          >
            {PRODUCT_CATEGORIES.map((category) => {
              const isSelected = category.type === selectedType;
              
              return (
                <Box
                  key={category.type}
                  borderWidth="2px"
                  borderRadius="lg"
                  borderColor={isSelected ? selectedBorder : borderColor}
                  p={{ base: 2, sm: 3, md: 4 }}
                  cursor={category.enabled ? "pointer" : "not-allowed"}
                  opacity={category.enabled ? 1 : 0.5}
                  transition="all 0.2s"
                  bg={isSelected ? selectedBg : 'transparent'}
                  onClick={() => handleSelect(category)}
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  w={{ base: '100px', sm: '150px', md: '180px', lg: '200px' }}
                  minW={{ base: '100px', sm: '150px', md: '180px', lg: '200px' }}
                  maxW="250px"
                  h={{ base: 'auto', md: '60vh' }}
                  maxH={{ base: 'none', md: '500px' }}
                  minH={{ base: '150px', md: '300px' }}
                  flexGrow={1}
                  flexShrink={1}
                  flexBasis="0"
                  position="relative"
                  transformOrigin="center center"
                  _hover={{
                    borderColor: category.enabled ? selectedBorder : borderColor,
                    transform: category.enabled ? 'scale(1.02)' : 'none',
                    boxShadow: category.enabled ? 'lg' : 'none',
                    zIndex: 1
                  }}
                >
                  <VStack 
                    spacing={{ base: 2, md: 4 }} 
                    align="stretch"
                    h="100%"
                  >
                    <Box 
                      flex="1"
                      minH={0}
                      display="flex"
                      alignItems="center"
                    >
                      <Image
                        src={category.image}
                        alt={category.title}
                        width="100%"
                        height="auto"
                        maxH="100%"
                        objectFit="contain"
                        fallbackSrc="https://via.placeholder.com/450"
                      />
                    </Box>
                    <Box flexShrink={0}>
                      <Text 
                        fontWeight="bold" 
                        fontSize={{ base: 'sm', sm: 'md', md: 'lg' }}
                        textAlign="center"
                        color={category.enabled ? "inherit" : "gray.400"}
                        noOfLines={2}
                      >
                        {category.title}
                      </Text>
                      <Text
                        fontSize={{ base: 'xs', sm: 'sm' }}
                        textAlign="center"
                        color={category.enabled ? "gray.600" : "gray.400"}
                        mt={{ base: 1, md: 2 }}
                        noOfLines={2}
                      >
                        {category.description}
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              );
            })}
          </Flex>
        </Box>
      </Container>
    </Box>
  );
}

SelectProductType.propTypes = {
  onSelect: PropTypes.func.isRequired,
};

export default SelectProductType; 